import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getPulseCardsSummary,
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  listPulseCards,
} from '../../services/pulseApi';
import {
  PULSE_CATEGORIES,
  PULSE_STATUSES,
  formatDeckPosition,
  formatNumber,
  formatPulseDate,
  isMatchRatingCard,
  optionsSummary,
  PULSE_DECK_CYCLE,
  parseCardsPayload,
  parseCardsSummaryPayload,
  sourceTypeLabel,
  statusChipColor,
} from './pulseUtils';

const PAGE_SIZE = 30;

function PulseCardList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(parseCardsSummaryPayload(null));

  const applied = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || '',
      category: searchParams.get('category') || '',
      match_id: searchParams.get('match_id') || '',
      deck: searchParams.get('deck') || '',
      order: searchParams.get('order') || 'app',
      offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
    }),
    [searchParams]
  );

  const [search, setSearch] = useState(applied.search);
  const [status, setStatus] = useState(applied.status);
  const [category, setCategory] = useState(applied.category);
  const [matchId, setMatchId] = useState(applied.match_id);
  const [deck, setDeck] = useState(applied.deck);
  const [order, setOrder] = useState(applied.order);

  useEffect(() => {
    setSearch(applied.search);
    setStatus(applied.status);
    setCategory(applied.category);
    setMatchId(applied.match_id);
    setDeck(applied.deck);
    setOrder(applied.order);
  }, [applied]);

  const writeParams = useCallback(
    (next) => {
      const params = {};
      if (next.search.trim()) params.search = next.search.trim();
      if (next.status) params.status = next.status;
      if (next.category) params.category = next.category;
      if (next.match_id) params.match_id = String(next.match_id).trim();
      if (next.deck) params.deck = next.deck;
      if (next.order && next.order !== 'app') params.order = next.order;
      if (next.offset) params.offset = String(next.offset);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const loadSummary = useCallback(async () => {
    try {
      const result = await getPulseCardsSummary();
      if (!isPulseApiSuccess(result)) return;
      setSummary(parseCardsSummaryPayload(getPulsePayload(result)));
    } catch {
      /* summary is non-blocking */
    }
  }, []);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseCards({
        limit: PAGE_SIZE,
        offset: applied.offset,
        status: applied.status || undefined,
        category: applied.category || undefined,
        match_id: applied.match_id || undefined,
        search: applied.search.trim() || undefined,
        deck: applied.deck || undefined,
        order: applied.order === 'updated' ? 'updated' : 'app',
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse cards.'));
        setItems([]);
        setTotal(0);
        return;
      }
      const parsed = parseCardsPayload(getPulsePayload(result));
      setItems(parsed.items);
      setTotal(parsed.total);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse cards.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const applyFilters = () => {
    writeParams({ search, status, category, match_id: matchId, deck, order, offset: 0 });
  };

  const hasMore = applied.offset + items.length < total;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Pulse cards</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => {
              loadCards();
              loadSummary();
            }}
            disabled={loading}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
          <Button
            component={Link}
            to="/admin/pulse/knowledge"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Daily knowledge
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Default list is the <strong>app push order</strong>. Each cycle is {PULSE_DECK_CYCLE}, then
        repeat. Do not re-sort. <code>deck_position</code> 1 =
        first card the app
        pushes; <code>—</code> = not on the live deck (those rows come after). A category filter keeps
        that type’s relative app order only — leave it empty to check the mix. Never delete a card.
        Use <Link to="/admin/pulse/deck">App deck</Link> for the live-only preview.
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip
          color="success"
          label={`Active ${formatNumber(summary.total_active)}`}
          onClick={() => writeParams({ ...applied, status: 'active', offset: 0 })}
        />
        {PULSE_CATEGORIES.filter((c) => c.value).map((c) => (
          <Chip
            key={c.value}
            variant="outlined"
            label={`${c.label} ${formatNumber(summary.by_category[c.value] || 0)}`}
            onClick={() => writeParams({ ...applied, category: c.value, offset: 0 })}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          placeholder="Title or summary"
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {PULSE_STATUSES.map((s) => (
            <MenuItem key={s.value || 'all'} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {PULSE_CATEGORIES.map((c) => (
            <MenuItem key={c.value || 'all'} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Match ID"
          value={matchId}
          onChange={(e) => setMatchId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          sx={{ width: 140 }}
        />
        <TextField
          select
          size="small"
          label="Deck"
          value={deck}
          onChange={(e) => setDeck(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Live deck only</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Order"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="app">App order</MenuItem>
          <MenuItem value="updated">Last edited (archive)</MenuItem>
        </TextField>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={applyFilters}>
          Apply
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell title="App push order. 1 = first card. — = not on the live deck">#</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Match</TableCell>
              <TableCell>Reward / Fee</TableCell>
              <TableCell>Locked</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No Pulse cards found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((card) => (
                <TableRow
                  key={card.id}
                  hover
                  component={Link}
                  to={`/admin/pulse/cards/${encodeURIComponent(card.id)}`}
                  sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                >
                  <TableCell>
                    {formatDeckPosition(card.deck_position ?? card.deckPosition)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={card.status} color={statusChipColor(card.status)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {card.title || 'Untitled'}
                    </Typography>
                    {card.summary && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {card.summary}
                      </Typography>
                    )}
                    {card.source_type && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {sourceTypeLabel(card.source_type)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {card.category || card.card_type || card.bucket || '—'}
                    </Typography>
                    {isMatchRatingCard(card) && (
                      <Chip size="small" label="Rate Player" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary">
                      {optionsSummary(card)}
                    </Typography>
                  </TableCell>
                  <TableCell>{card.match_id ?? '—'}</TableCell>
                  <TableCell>
                    {card.reward_fe ?? 0} / {card.entry_fee_fe ?? 0} FE
                  </TableCell>
                  <TableCell>
                    {card.options_locked ? (
                      <Chip size="small" color="warning" label="locked" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{formatPulseDate(card.created_at || card.createdAt)}</TableCell>
                  <TableCell>{formatPulseDate(card.expires_at ?? card.expiresAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={applied.offset <= 0 || loading}
          onClick={() => writeParams({ ...applied, offset: Math.max(0, applied.offset - PAGE_SIZE) })}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total === 0
            ? '0 cards'
            : `${applied.offset + 1}–${applied.offset + items.length} of ${total}`}
        </Typography>
        <Button
          disabled={!hasMore || loading}
          onClick={() => writeParams({ ...applied, offset: applied.offset + PAGE_SIZE })}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default PulseCardList;
